import { Button, Empty } from 'antd';
import { CreateButton } from '../../../components/action-buttons';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useDoc31Store } from '../../../contexts/mobx';
import { DOC31_DEFAULT_TIMEZONE } from '../../../utils/doc31';
import { useDoc31ReasonPrompt } from '../reason-prompt';
import { CouponDrawer } from './coupon-drawer';
import { CouponTable } from './coupon-table';
import { drawWeights } from './form';
import style from './style.module.scss';

interface Doc31CouponsTabProps {
    /** A coupon to open, e.g. from a readiness issue; null for none. */
    focusCouponId: number | null;
    onFocusHandled: () => void;
}

/** Which drawer is open: a coupon by id, or a new one (`null`). */
interface OpenDrawer {
    promotionId: number | null;
}

export const Doc31CouponsTab: React.FC<Doc31CouponsTabProps> = observer(
    ({ focusCouponId, onFocusHandled }) => {
        const store = useDoc31Store();
        const { prompt, element } = useDoc31ReasonPrompt();
        const [drawer, setDrawer] = useState<OpenDrawer | null>(null);

        const { coupons } = store;
        const timezone = store.view?.timezone ?? DOC31_DEFAULT_TIMEZONE;
        const draw = drawWeights(coupons);

        const focusFound =
            focusCouponId !== null &&
            coupons.some((coupon) => coupon.promotionId === focusCouponId);
        useEffect(() => {
            if (focusFound) {
                setDrawer({ promotionId: focusCouponId });
                onFocusHandled();
            }
        }, [focusFound, focusCouponId, onFocusHandled]);

        const editing =
            drawer?.promotionId == null
                ? null
                : coupons.find(
                      (coupon) => coupon.promotionId === drawer.promotionId
                  ) ?? null;

        const addButton = store.isReadOnly ? null : (
            <CreateButton onClick={() => setDrawer({ promotionId: null })}>
                Add coupon
            </CreateButton>
        );

        const isEmpty = coupons.length === 0 && !store.isLoadingEdition;

        if (store.couponsLoadFailed && !store.isLoadingEdition) {
            return (
                <div className={style.tab}>
                    <Empty description="Couldn't load coupons.">
                        <Button onClick={() => store.fetchCoupons()}>
                            Try again
                        </Button>
                    </Empty>
                </div>
            );
        }

        return (
            <div className={style.tab}>
                <div className={style.top_line}>
                    <div className={style.summary}>
                        <span className={style.count}>
                            {coupons.length}{' '}
                            {coupons.length === 1 ? 'coupon' : 'coupons'}
                        </span>
                        {!isEmpty && (
                            <span className={`${style.numeric} ${style.muted}`}>
                                {draw.count === 0
                                    ? 'None in the draw yet.'
                                    : `${
                                          draw.count
                                      } in the draw · total weight ${draw.totalWeight.toLocaleString()}`}
                            </span>
                        )}
                    </div>
                    {!isEmpty && addButton}
                </div>

                {isEmpty ? (
                    <div className={style.empty}>
                        <p>
                            {store.isReadOnly
                                ? 'No coupons in this edition.'
                                : 'No coupons yet. Add the first one.'}
                        </p>
                        {addButton}
                    </div>
                ) : (
                    <CouponTable
                        coupons={coupons}
                        timezone={timezone}
                        totalWeight={draw.totalWeight}
                        loading={store.isLoadingEdition}
                        onOpen={(promotionId) => setDrawer({ promotionId })}
                    />
                )}

                {drawer && (drawer.promotionId === null || editing) && (
                    <CouponDrawer
                        key={drawer.promotionId ?? 'new'}
                        coupon={editing}
                        timezone={timezone}
                        otherDrawWeight={
                            draw.totalWeight -
                            (editing?.selection.status === 'active'
                                ? editing.selection.weight
                                : 0)
                        }
                        prompt={prompt}
                        onClose={() => setDrawer(null)}
                    />
                )}
                {element}
            </div>
        );
    }
);
